import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import { usePaymentDebitNoteListAPI } from "@/app/hooks/api_hooks/payment/usePaymentDebitNoteListAPI";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useUtils } from "@/app/hooks/useUtils";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useState } from "react";
import { FcCancel } from "react-icons/fc";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import DebitNoteApproveModal from "../DebitNoteApproveModal";
import { useSearchParams } from "react-router-dom";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useForm } from "react-hook-form";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import Input from "@/app/components/ui/Inputs/Input";
import { usePaymentFinanceDebitNoteListAPI } from "@/app/hooks/api_hooks/payment/usePaymentFinanceDebitNoteListAPI";
import { ICartState, setDebitNoteFilter } from "@/app_redux/reducers/slices/auth/cart_slice";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { usePaymentDebitStatusListAPI } from "@/app/hooks/api_hooks/payment/usePaymentDebitStatusListAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IDebitNoteFilter } from "@/api_framework/api_modals/payment";
import { CiShare1 } from "react-icons/ci";
import { ROUTES } from "@/app/routes";
import { PhoneIcon } from "@heroicons/react/24/outline";
import RBACFlag from "@/app/components/core/RBACFlag";

export interface InvoiceFilter {
  status: Array<string>;
  user: string;
}

const DebitNoteListComponent = () => {
  const { getRouteKey } = useRouter();
  const { veticDateFormat, statusBackgroundColor } = useUtils();
  const dispatch = useAppDispatch();
  const { NO_CART } = useImageConstants();
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;

  const { debitNoteList } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;

  const isTransactionLoading = useLoader("isCustomerDebitNoteTableInProgress");

  /* Calendar Releated Code*/
  const [selectionDate, setSelectionDate] = useState({
    startDate: null as Date | null,
    endDate: null as Date | null,
    key: "selectionDate",
  });

  const formatDate = (date: Date | null) => {
    if (!date) return null;
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
  };
  const formattedStartDate = formatDate(selectionDate.startDate);
  const formattedEndDate = formatDate(selectionDate.endDate);

  const [open, setOpen] = useState(false);
  const [debitNoteReceiptId, setDebitNoteReceiptId] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [userId, setUserId] = useState(null);
  const [initial, setInitial] = useState(false);

  const [params, _setParams] = useSearchParams();

  const isCustomerLabel = params.get("from_cart");

  const { execute: fetchDebitNote } = usePaymentDebitNoteListAPI();
  const { execute: fetchDebitNoteFinanceList } = usePaymentFinanceDebitNoteListAPI();
  const { execute: fetchStatusList, debitStatus } = usePaymentDebitStatusListAPI();

  const { forAlphaNumeric } = useFormValidations();

  const formHook = useForm<InvoiceFilter>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    if (formHook.getValues("status") && !initial && !isCustomerLabel) {
      fetchDebitNote("", formattedStartDate ?? "", formattedEndDate ?? "", formHook.getValues("status")?.join(","));
      const constructedBody: IDebitNoteFilter = {
        enddate: formattedEndDate ?? "",
        startdate: formattedStartDate ?? "",
        status: formHook.getValues("status")?.join(","),
        user: "",
      };
      dispatch(setDebitNoteFilter(constructedBody));
      setInitial(true);
    }
  }, [isCustomerLabel, formHook.watch("status"), initial]);

  useEffect(() => {
    if (formHook.getValues("status") && !initial && isCustomerLabel) {
      fetchDebitNote(customerId, formattedStartDate ?? "", formattedEndDate ?? "", formHook.getValues("status")?.join(","));
      const constructedBody: IDebitNoteFilter = {
        enddate: formattedEndDate ?? "",
        startdate: formattedStartDate ?? "",
        status: formHook.getValues("status")?.join(","),
        user: customerId,
      };
      dispatch(setDebitNoteFilter(constructedBody));
      setInitial(true);
    }
  }, [customerId, formHook.watch("status"), initial, isCustomerLabel]);

  const onSubmit = (data: InvoiceFilter) => {
    if (isCustomerLabel) {
      fetchDebitNote(customerId, formattedStartDate ?? "", formattedEndDate ?? "", data.status?.join(","));
    } else {
      fetchDebitNoteFinanceList(formattedStartDate ?? "", formattedEndDate ?? "", data.user, data.status?.join(","));
    }
    const constructedBody: IDebitNoteFilter = {
      enddate: formattedEndDate ?? "",
      startdate: formattedStartDate ?? "",
      status: data.status?.join(","),
      user: isCustomerLabel ? customerId : formHook.getValues("user"),
    };
    dispatch(setDebitNoteFilter(constructedBody));
  };

  const debitStatusItem = useSelectMenuReducer(debitStatus, "value", "id");

  useEffect(() => {
    fetchStatusList();
  }, []);

  const handelToUserProfile = (userId: string) => {
    const url = `${ROUTES.ROOT.children.CUSTOMER_PROFILE.children.DETAILS_CUSTOMER.url}/appointments/${userId}?from_om=true`;
    window.open(url, "_blank");
  };

  return (
    <div className="h-full ">
      <div className="bg-white p-2 sm:p-3">
        <p className="text-sm font-semibold pb-1 mb-1">Debit Note</p>

        <form onSubmit={formHook.handleSubmit(onSubmit)}>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
            {/* Calendar Component */}
            <OMCalendarComponent
              selectionDate={{
                startDate: selectionDate.startDate ?? new Date(),
                endDate: selectionDate.endDate ?? new Date(),
                key: "selectionDate",
              }}
              setSelectionDate={(val: any) => setSelectionDate(val)}
              startDate={formattedStartDate ?? "Select Date"}
              endDate={formattedEndDate ?? "Select Date"}
              handelClick={() => {
                // handelClick();
              }}
            />

            {/* Multi-Select Menu */}
            <div className="w-full sm:w-56">
              <SearchableMultiSelectMenu
                onSelectItem={(items) => {
                  if (items) {
                    const status = items.map((i) => i.id);
                    formHook.setValue("status", status);
                  }
                }}
                withSelectAll
                defaultSelectAll={true}
                showTooltips
                withDropDownSearch
                errorMessages={[{ message: "Required", type: "required" }]}
                fieldError={formHook.formState.errors.status}
                register={formHook.register("status", {
                  required: true,
                })}
                showTypedErrors={false}
                containerClassName="ml-0 sm:ml-2"
                placeholder="Select Status"
                defaultSelected={debitStatusItem?.filter((c) => formHook.watch("status")?.includes(c.id))}
                selectItems={debitStatusItem}
              />
            </div>

            {/* Input Field */}
            {!isCustomerLabel && (
              <div className="col-span-1 w-full sm:w-56">
                <Input
                  className="w-full text-xs"
                  placeholder="Search by Mobile Number"
                  maxLength={10}
                  register={formHook.register("user", {
                    required: false,
                    ...forAlphaNumeric.validations,
                  })}
                  fieldError={formHook.formState.errors.user}
                  errorMessages={[forAlphaNumeric.errors, { message: "Number is required", type: "required" }]}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              className="bg-[var(--color-primary)] text-xs px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg text-white font-medium w-full sm:w-auto"
              type="submit"
            >
              Apply
            </button>
          </div>
        </form>
      </div>
      {isTransactionLoading ? (
        <TableSkeleton />
      ) : debitNoteList?.length > 0 ? (
        <div className="mt-2 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto">
          <div className="w-full">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full min-w-[1100px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                  <tr>
                    {(isCustomerLabel ? tableHeaderCustomerLevel : tableHeader).map((key, index) => (
                      <th
                        key={index}
                        scope="col"
                        className={`p-2 py-3 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider ${key.item === "Action" ? " border-[#E5E7EB]" : ""}`}
                      >
                        {key.item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {debitNoteList?.map((cl, index) => {
                    const isFirst = index === 0;
                    const isLast = index === debitNoteList.length - 1;
                    const isSingle = debitNoteList.length === 1;

                    return (
                      <tr
                        key={index}
                        className={`
                          bg-white
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                      >
                        {!isCustomerLabel && (
                          <td
                            className={`
                              whitespace-nowrap px-2 py-3 text-xs text-black
                              ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                              ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                              ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                            `}
                          >
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs">{cl?.name}</span>
                              <span className="flex items-center text-xs text-black">
                                {cl?.mobile !== null && (
                                  <span className="flex flex-row items-center gap-1">
                                    {cl?.mobile} {cl?.mobile && <PhoneIcon className="w-3 h-3" />}
                                  </span>
                                )}
                              </span>
                              <p
                                className="text-[#0068FF] font-medium flex flex-row items-center gap-1 text-xs cursor-pointer"
                                onClick={() => handelToUserProfile(cl?.user_id)}
                              >
                                Customer Profile <CiShare1 className="w-4 h-4 font-semibold cursor-pointer" />
                              </p>
                            </div>
                          </td>
                        )}

                        <td
                          className={`
                            whitespace-nowrap px-2 py-3 text-xs text-black
                            ${isCustomerLabel && isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isCustomerLabel && isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isCustomerLabel && isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                        >
                          {cl?.debit_note_receipt_id}
                        </td>

                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">{cl?.credit_note_receipt_id}</td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                          <button
                            className={`${statusBackgroundColor(cl?.status?.toLowerCase())} py-1 px-3 rounded-full text-[#000000] capitalize`}
                          >
                            {cl?.status}
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">{cl?.debit_amount}</td>
                        <td className="whitespace-nowrap px-2 py-3 text-xs text-black">
                          {cl?.created_at ? veticDateFormat(cl?.created_at) : "NA"}
                        </td>

                        <td className="px-2 py-3 text-xs text-black max-w-[220px] sm:max-w-[340px] whitespace-normal break-words">
                          {cl?.comments}
                        </td>
                        <td
                          className={`
                            whitespace-nowrap px-2 py-3 text-xs text-black border-[#E5E7EB]
                            ${isCustomerLabel ? (isSingle ? "rounded-tr-2xl rounded-br-2xl" : "") : ""}
                            ${isCustomerLabel && isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isCustomerLabel && isLast && !isSingle ? "rounded-br-2xl" : ""}
                            ${!isCustomerLabel && (isSingle ? "rounded-tr-2xl rounded-br-2xl" : "")}
                            ${!isCustomerLabel && isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${!isCustomerLabel && isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                        >
                          <RBACFlag actionId="DEBIT_NOTE_REQUEST_ACTION_CTA">
                            {cl?.status === "pending" && (
                              <div className="flex items-center gap-4">
                                <div
                                  className="text-[#22c55e] flex flex-col gap-2 items-center cursor-pointer"
                                  onClick={() => {
                                    setOpen(true);
                                    setApprovalStatus("Approve");
                                    setDebitNoteReceiptId(cl?.debit_note_receipt_id);
                                    setUserId(cl?.user_id);
                                  }}
                                >
                                  <p>
                                    <IoCheckmarkCircleOutline className="w-4 h-4" color="#22c55e" />
                                  </p>
                                  <p>Approve</p>
                                </div>
                                <div
                                  className="text-[#E11D48] flex flex-col gap-2 items-center cursor-pointer"
                                  onClick={() => {
                                    setOpen(true);
                                    setApprovalStatus("Reject");
                                    setDebitNoteReceiptId(cl?.debit_note_receipt_id);
                                    setUserId(cl?.user_id);
                                  }}
                                >
                                  <p>
                                    <FcCancel className="w-4 h-4" />
                                  </p>
                                  <p>Reject</p>
                                </div>
                              </div>
                            )}
                          </RBACFlag>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <DebitNoteApproveModal
                  userId={userId}
                  debitNoteReceiptId={debitNoteReceiptId}
                  open={open}
                  setOpen={setOpen}
                  approvalStatus={approvalStatus}
                />
              </table>
            </div>
          </div>
        </div>
      ) : (
        debitNoteList?.length === 0 && (
          <div className="h-[500px] bg-white">
            <NoItemFoundComponent
              message=""
              description="No Debit Note Found"
              imageURL={NO_CART}
              InputClassName="my-0 h-full"
            />
          </div>
        )
      )}
    </div>
  );
};

export default DebitNoteListComponent;
const tableHeaderCustomerLevel = [
  { item: "Debit note ID" },
  { item: "Credit note ID" },
  { item: "DN Status" },
  { item: "Amount" },
  { item: "Date" },
  { item: "Comment" },
  { item: "Action" },
];
const tableHeader = [
  { item: "Client Details" },
  { item: "Debit note ID" },
  { item: "Credit note ID" },
  { item: "DN Status" },
  { item: "Amount" },
  { item: "Date" },
  { item: "Comment" },
  { item: "Action" },
];
const TableSkeleton = () => {
  return (
    <div className="mt-3 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto animate-pulse">
      <div className="w-full">
        <div className="inline-block min-w-full align-middle">
          <table className="w-full min-w-[1100px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
            <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
              <tr>
                {[...Array(8)].map((_, index) => (
                  <th key={index} className="p-2 py-3 text-left text-xs font-medium text-gray-300 tracking-wider">
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(8)].map((_, rowIndex) => (
                <tr key={rowIndex} className="bg-white">
                  {[...Array(7)].map((_, colIndex) => (
                    <td key={colIndex} className="px-2 py-3 whitespace-nowrap text-xs text-gray-300">
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </td>
                  ))}
                  <td className="px-2 py-3 text-xs text-gray-300 max-w-[220px] sm:max-w-[340px] whitespace-normal">
                    <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
